import { useSession } from "@/lib/auth-client";
import { useSyncExternalStore } from "react";

// Super-admin-only surfaces inside the customer dashboard stay hidden until the
// operator switches them on from the floating dock, so a screen-shared call
// shows what the customer sees. Per tab (sessionStorage): every new tab starts
// hidden. The switch is presentation only — the backend still authorizes the
// super admin either way.
const KEY = "boo.superadmin-view";
const listeners = new Set<() => void>();

function read(): boolean {
  try {
    return sessionStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

let on = read();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setSuperAdminView(next: boolean) {
  on = next;
  try {
    if (next) sessionStorage.setItem(KEY, "1");
    else sessionStorage.removeItem(KEY);
  } catch {
    // Storage unavailable: the switch still works for this page load.
  }
  for (const l of listeners) l();
}

export function useIsSuperAdmin(): boolean {
  const { data: session } = useSession();
  return (session?.user as { role?: string } | undefined)?.role === "super_admin";
}

// True only for a super admin who has the dock switched on. Use this, not the
// raw role, to gate super-admin extras on customer-facing pages.
export function useSuperAdminView(): boolean {
  const isSuperAdmin = useIsSuperAdmin();
  const enabled = useSyncExternalStore(subscribe, () => on);
  return isSuperAdmin && enabled;
}
